import React from 'react';
import { WEEKDAYS_SHORT, formatTime, formatScheduleLabel, lessonFormForHeadcount } from '@/lib/utils';
import { toISO, getKW, compactLessonLabel } from './calendarUtils';

function MonthEventButton({ event, onClick, children }) {
  return (
    <button
      type="button"
      className="w-full text-left text-[10px] leading-tight truncate px-0.5 hover:bg-slate-100 rounded"
      data-calendar-event=""
      onClick={(ev) => { ev.stopPropagation(); onClick?.(event); }}
    >
      {children}
    </button>
  );
}

export default function MonthGrid({
  weeks, entries, holidays, currentDate, todayIso, onEventClick, onDayClick, maxPerCell = 5,
}) {
  const byDay = {};
  for (const e of entries) {
    (byDay[e.date] = byDay[e.date] || []).push(e);
  }
  Object.values(byDay).forEach((list) => list.sort((a, b) => String(a.start).localeCompare(String(b.start))));

  const holidayOn = (iso) => holidays.find((h) => iso >= h.start_date && iso <= h.end_date);

  return (
    <div className="flex-1 overflow-auto bg-white" data-testid="calendar-month-grid">
      <div className="grid grid-cols-[40px_repeat(7,minmax(0,1fr))] border-b border-slate-200 sticky top-0 z-10 bg-white">
        <div className="text-[10px] text-slate-400 p-1">KW</div>
        {WEEKDAYS_SHORT.map((d) => (
          <div key={d} className="text-xs font-semibold text-slate-600 p-1 text-center border-l border-slate-100">{d}</div>
        ))}
      </div>
      {weeks.map((days, wi) => (
        <div key={wi} className="grid grid-cols-[40px_repeat(7,minmax(0,1fr))] min-h-[110px] border-b border-slate-200">
          <div className="text-[10px] text-slate-400 p-1">{getKW(days[0])}</div>
          {days.map((day) => {
            const iso = toISO(day);
            const inMonth = day.getMonth() === currentDate.getMonth();
            const hol = holidayOn(iso);
            const list = byDay[iso] || [];
            const extra = Math.max(0, list.length - maxPerCell);
            const shown = list.slice(0, maxPerCell);
            const isToday = iso === todayIso;
            const selected = iso === toISO(currentDate);
            return (
              <div key={iso}
                   className={`border-l border-slate-100 p-0.5 cursor-pointer ${
                     selected ? 'bg-brand-red/5' : isToday ? 'bg-brand-red/[0.04]' : inMonth ? 'bg-white' : 'bg-slate-50'
                   }`}
                   data-testid={`calendar-month-day-${iso}`}
                   onClick={() => onDayClick?.(day)}>
                {hol && (
                  <div className="text-[9px] font-semibold text-white bg-brand-red rounded-sm px-1 truncate mb-0.5">
                    {hol.name}{hol.type === 'ferien' ? ' (Ferien)' : ''}
                  </div>
                )}
                <div className={`text-[11px] text-right pr-1 ${inMonth ? 'text-slate-700' : 'text-slate-300'}`}>
                  {day.getDate()}
                </div>
                {shown.map((e, i) => {
                  const privacy = e.privacy;
                  const title = privacy === 'busy'
                    ? 'Belegt'
                    : compactLessonLabel(lessonFormForHeadcount(
                      formatScheduleLabel(e.name) || e.name || '',
                      Array.isArray(e.students) ? e.students.length : 0,
                    ));
                  const primary = privacy === 'busy'
                    ? 'Belegt'
                    : privacy === 'course'
                      ? (title || e.teacher_name || 'Unterricht')
                      : (e.student_label || e.teacher_name || title);
                  return (
                  <MonthEventButton
                    key={`${e.course_id}-${i}`}
                    event={e}
                    onClick={onEventClick}
                  >
                    <span className="font-mono">{formatTime(e.start)}</span>{' '}
                    <span className="inline-block w-1.5 h-1.5 rounded-full mr-0.5 align-middle bg-slate-500" />
                    {primary}
                    {title && primary !== title ? ` / ${title}` : ''}
                  </MonthEventButton>
                  );
                })}
                {extra > 0 && (
                  <div className="text-[10px] text-blue-700 px-0.5">+ weitere {extra}</div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
